import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';
import { TooltipProps } from '@growtherapy/sprout-ui';

import { ControlPanelButton, ControlPanelButtonTooltip } from '../../components';
import { logger } from '../../datadog';
import { useGetTelehealthSessionInfo } from '../../hooks/useGetTelehealthSessionInfo';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { RecordingControl as BaseRecordingControl } from '../../session/controls/RecordingControl';
import { RecordingControlMultipleParticipantsToast } from '../../session/controls/RecordingControlMultipleParticipantsToast';
import { CONSENT_TOAST_DURATION_MS, TRANSCRIPTION_DISABLED_TOAST_TIMEOUT } from '../../session/controls/constants';
import { useShouldDisableTranscriptionForMultipleParticipants } from '../../session/controls/useShouldDisableTranscriptionForMultipleParticipants';
import { patientConsentAtom, providerConsentAtom } from '../../session/messages/state';
import { toast } from '../../toasts';
import { UserType } from '../../types';
import { AudioRecordingState, ConsentStatus, recordingStateAtom } from '../../video-sdk';
import { useSendVisitorParticipantStateCallback } from '../messages/useSendVisitorParticipantState';
import { ConsentDeclinedToast } from './ConsentDeclinedToast';
import { ConsentModalWrapper as ConsentModal } from './ConsentModalWrapper';
import { RecordingNoticeWrapper as RecordingNotice } from './RecordingNoticeWrapper';
import { useRecordingControl } from './useRecordingControl';

export function RecordingControl() {
  const t = useTranslate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showRecordingNotice, setShowRecordingNotice] = useState(false);
  const [hasShownRecordingNotice, setHasShownRecordingNotice] = useState(false);
  const [shouldShowConsentDeclinedToast, setShouldShouldConsentDeclinedToast] = useState(false);

  const [hasShownMultipleParticipantsNotice, setHasShownMultipleParticipantsNotice] = useState(false);
  const { userClicked } = useTrackEvent();
  const { providerShortId, patientShortId } = useParams();

  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const appointmentShortId = data?.telehealthSessionInfo?.appointment?.shortId;
  const { isVisible, recordingState } = useRecordingControl();
  const setRecordingState = useSetAtom(recordingStateAtom);
  const [patientConsentState, setPatientConsent] = useAtom(patientConsentAtom);
  const patientConsent = patientConsentState.value;
  const providerConsent = useAtomValue(providerConsentAtom).value;

  const toggleRecording = (shouldRecord: boolean) => {
    setShowRecordingNotice(false);
    setRecordingState({
      value: shouldRecord ? AudioRecordingState.RECORDING : AudioRecordingState.NOT_RECORDING,
      lastUpdated: Date.now(),
    });
    sendParticipantState();
  };
  const sendParticipantState = useSendVisitorParticipantStateCallback();
  const bothPartiesConsented = patientConsent === ConsentStatus.OPTED_IN && providerConsent === ConsentStatus.OPTED_IN;
  const isRecording = recordingState === AudioRecordingState.RECORDING;

  useEffect(
    function notifyPatientWhenRecordingFirstStarts() {
      if (isRecording && !hasShownRecordingNotice) {
        setShowRecordingNotice(true);
        setHasShownRecordingNotice(true);
      }
    },
    [isRecording, hasShownRecordingNotice],
  );

  useEffect(
    function openConsentModalWhenPatientConsentIsDeciding() {
      if (patientConsent === ConsentStatus.DECIDING) {
        setIsModalOpen(true);
      }
    },
    [patientConsent],
  );

  const closeConsentModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  /**
   * The below code is to prevent sessions with multiple participants from surfacing
   * Transcription features due to AI quality issues identified in these types of sessions.
   * It is behind a feature flag and can be removed when we improve AI features for these sessions.
   */

  const shouldDisableTranscriptionForMultipleParticipants = useShouldDisableTranscriptionForMultipleParticipants();

  const shouldShowMultipleParticipantTranscriptionDisabledToast =
    shouldDisableTranscriptionForMultipleParticipants && isRecording && !hasShownMultipleParticipantsNotice;

  useEffect(
    function showRecordingStatusToasts() {
      if (shouldShowMultipleParticipantTranscriptionDisabledToast) {
        toast.enqueue(RecordingControlMultipleParticipantsToast, {
          timeout: TRANSCRIPTION_DISABLED_TOAST_TIMEOUT,
        });
        setHasShownMultipleParticipantsNotice(true);
      }

      if (shouldShowConsentDeclinedToast && !isModalOpen) {
        toast.enqueue((ariaProps) => <ConsentDeclinedToast {...ariaProps} visitorDeclinedConsent />, {
          timeout: CONSENT_TOAST_DURATION_MS,
        });
        setShouldShouldConsentDeclinedToast(false);
      }
    },
    [shouldShowMultipleParticipantTranscriptionDisabledToast, shouldShowConsentDeclinedToast, isModalOpen],
  );

  const tooltipProps = useMemo(
    () =>
      showRecordingNotice
        ? ({
            className: 'opacity-100 z-0 rebrand:!bg-surface-inverseEmphasis',
            isOpen: true,
            clickable: true,
            text: <RecordingNotice onClose={() => setShowRecordingNotice(false)} />,
          } satisfies Partial<TooltipProps>)
        : undefined,
    [showRecordingNotice],
  );

  if (!isVisible) return null;

  return (
    <>
      <ConsentModal
        isOpen={isModalOpen}
        onClose={closeConsentModal}
        onStartRecording={() => setShowRecordingNotice(false)}
        setShouldShowClientConsentDeclinedToast={setShouldShouldConsentDeclinedToast}
        setPatientConsent={setPatientConsent}
      />
      {recordingState === AudioRecordingState.DISABLED || shouldDisableTranscriptionForMultipleParticipants ? (
        <ControlPanelButtonTooltip
          text={t('telehealth-core:recording.transcriptionIsDisabled')}
          /**
           * Although this Tooltip wraps a ControlPanelButton, the tooltip only ever
           * shows when the ControlPanelButton is disabled. In order to make this
           * tooltip still accessible for all users, we wrap it in a <div/> so that
           * there is an element to add the tooltip props to and set
           * childIsInteractive={false} (since the ControlPanelButton won't be
           * actually interactive when the tooltip is enabled).
           */
          childIsInteractive={false}
        >
          <div>
            <ControlPanelButton
              disabled={true}
              aria-label={t('telehealth-core:recording.transcriptionIsDisabled')}
              iconDefinition={faPlay}
              data-testid='recording-control'
            />
          </div>
        </ControlPanelButtonTooltip>
      ) : (
        (bothPartiesConsented || isRecording) && (
          <BaseRecordingControl
            className='bg-lilac-700 hover:bg-lilac-600'
            isRecording={isRecording}
            onRecordingToggle={(shouldRecord: boolean) => {
              toggleRecording(shouldRecord);
              logger.info(`Client manually toggled recording ${shouldRecord ? 'on' : 'off'}`, {
                providerShortId,
                patientShortId,
                appointmentShortId: appointmentShortId!,
              });
              userClicked(EventTypeEnum.CLICK_RECORDING_TOGGLE, {
                appointmentShortId: appointmentShortId!,
                newRecordingStatus: shouldRecord ? 'PLAY' : 'PAUSE',
                entityType: UserType.CLIENT,
              });
            }}
            consentPending={false} // Clients don't see this until they've consented
            tooltipProps={tooltipProps}
          />
        )
      )}
    </>
  );
}
